import type { AnyRouter } from '@tanstack/react-router';
import { redirect } from '@tanstack/react-router';
import { isModuleNotFoundError } from '@tanstack/router-core';
import { isDeferredRoutePath } from './deferred-routes';

/**
 * Loads the route areas the app does not boot with (see `deferred-routes.ts`).
 *
 * The router is created with the boot route tree, which leaves those areas out. The full tree,
 * `routeTree.gen.ts`, is a separate chunk: importing it attaches every route to the same root
 * route object, the deferred areas included. A router only reads its tree when it is handed
 * one, so its lookup tables are then rebuilt from the grown tree.
 *
 * Two callers:
 * - `main.tsx`, before the first render, when a hard load lands inside a deferred area;
 * - the root route's `beforeLoad`, when an in-app navigation enters one. It waits for the
 *   routes and redirects to the same location so the router matches it against the new table.
 *   If the chunk cannot be fetched, it reloads the document, which by then shows that location.
 */

/**
 * The two router methods `router.update()` itself uses to adopt a changed route tree, and the
 * state that names the page on screen while a navigation is in flight.
 */
type RouteTableRouter = Pick<AnyRouter, 'buildRouteTree' | 'setRoutes' | 'state'>;

type FullRouteTreeImport = () => Promise<unknown>;

/**
 * Reloads the document. The promise settles only if the document is still here afterwards, as
 * when the user stays at an unsaved-changes prompt; while the document unloads it never settles.
 */
type DocumentReload = () => Promise<void>;

/**
 * `pendingMs` for the root route, which the app's pending component would otherwise replace in
 * full (shell, navigation and the page being left) once a deferred area takes longer than the
 * router's default second to load. The root never shows one: the page being left, or the
 * interstitial that led here, stays on screen until the routes arrive. Below the root, routes
 * keep the default.
 */
export const ROOT_PENDING_MS = Infinity;

/**
 * How long a reload has to start unloading the document before it counts as cancelled. Chromium
 * returns from `reload()` and runs timers only once an unsaved-changes prompt is answered, so the
 * wait covers the time the server takes to answer the reload. A wait that runs out on a slow
 * answer is harmless: returning to the page rewrites the URL, which does not stop the reload.
 */
export const RELOAD_UNLOAD_WAIT_MS = 5000;

const importFullRouteTree: FullRouteTreeImport = () => import('@client/routeTree.gen');

/** Calls `reload` and settles if the document has not started to unload (`pagehide`) within `waitMs`. */
export function reloadUnlessCancelled(reload: () => void, waitMs = RELOAD_UNLOAD_WAIT_MS): Promise<void> {
    return new Promise((resolve) => {
        let unloading = false;
        const onPageHide = () => {
            unloading = true;
        };
        window.addEventListener('pagehide', onPageHide, { once: true });
        reload();
        setTimeout(() => {
            window.removeEventListener('pagehide', onPageHide);
            if (!unloading) resolve();
        }, waitMs);
    });
}

const reloadCurrentDocument: DocumentReload = () => reloadUnlessCancelled(() => window.location.reload());

let attached: { router: RouteTableRouter; importTree: FullRouteTreeImport; reload: DocumentReload } | undefined;
let loaded = false;
let inFlight: Promise<void> | undefined;
const listeners = new Set<() => void>();

/** Names the router the deferred routes join. Called once, where the router is created. */
export function attachDeferredRoutes(
    router: RouteTableRouter,
    importTree: FullRouteTreeImport = importFullRouteTree,
    reload: DocumentReload = reloadCurrentDocument,
) {
    attached = { router, importTree, reload };
    loaded = false;
    inFlight = undefined;
}

export function areDeferredRoutesLoaded(): boolean {
    return loaded;
}

/** Notifies when the deferred routes have joined the route table, for `useSyncExternalStore`. */
export function subscribeToDeferredRoutes(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/**
 * Adds the deferred route areas to the router. Concurrent and repeated calls share one load;
 * a failed load is forgotten so the next call tries again.
 */
export function loadDeferredRoutes(): Promise<void> {
    if (!attached) {
        return Promise.reject(new Error('Deferred routes requested before a router was attached.'));
    }
    const { router, importTree } = attached;
    inFlight ??= importTree().then(
        () => {
            router.setRoutes(router.buildRouteTree());
            loaded = true;
            for (const listener of listeners) listener();
        },
        (error: unknown) => {
            inFlight = undefined;
            throw error;
        },
    );
    return inFlight;
}

type DeferredRoutesBeforeLoadArgs = {
    location: { pathname: string; href: string };
    preload: boolean;
    /** The navigation's own controller, aborted when a later navigation supersedes it. */
    abortController: Pick<AbortController, 'signal'>;
};

/**
 * `beforeLoad` for the root route. Does nothing unless the navigation targets a deferred area
 * whose routes are not in the table yet; then it loads them and restarts the navigation.
 *
 * A preload (link hover) only warms the routes up. It must not redirect, and a failed warm-up
 * stays silent because the navigation that follows loads again and reports the failure.
 *
 * A chunk that cannot be fetched is nearly always a tab that outlived a deploy, as with any
 * lazy route chunk. The navigation then becomes a document load of the same location: that
 * brings the current build, where `main.tsx` loads the routes at boot and the chunk recovery in
 * `index.html` bounds any further failure. Other errors reach the root route's error component.
 *
 * The router has already committed the location to history when `beforeLoad` runs, so the
 * document is reloaded in place. A redirect to the same location would not reload it when the
 * URL has a fragment: assigning the current URL with a fragment is a same-document fragment
 * navigation. The navigation is held while the document unloads, so the old table never
 * matches it, and the root shows no pending component (`ROOT_PENDING_MS`), so the page being
 * left stays on screen meanwhile.
 *
 * If the document is still here once the reload's wait is over, the user stayed at an
 * unsaved-changes prompt. The navigation then returns to the page on screen, as a blocked
 * navigation would, so the URL matches it again; the page stays mounted with its changes, and
 * the next entry into the area loads again. A navigation started meanwhile takes over instead.
 * With no page to return to, the error reaches the root route's error component.
 *
 * Only the navigation still in flight reloads. One superseded while the routes load no longer
 * owns the location: the router discards whatever it throws, but it cannot undo a reload, which
 * would replace the page the later navigation reached. A later navigation into the area shares
 * the load and reloads in its place.
 */
export function deferredRoutesBeforeLoad({
    location,
    preload,
    abortController,
}: DeferredRoutesBeforeLoadArgs): void | Promise<void> {
    if (loaded || !isDeferredRoutePath(location.pathname)) {
        return;
    }
    if (preload) {
        loadDeferredRoutes().catch(() => undefined);
        return;
    }
    // The page on screen while this navigation is in flight.
    const leftHref = attached?.router.state.resolvedLocation?.href;
    return loadDeferredRoutes().then(
        () => {
            // The table changed under a navigation that was matched against the old one.
            throw redirect({ href: location.href, replace: true });
        },
        (error: unknown) => {
            if (!attached || !isModuleNotFoundError(error) || abortController.signal.aborted) {
                throw error;
            }
            return attached.reload().then(() => {
                if (leftHref === undefined) {
                    throw error;
                }
                throw redirect({ href: leftHref, replace: true, resetScroll: false, hashScrollIntoView: false });
            });
        },
    );
}
